import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import TambaLogo from "@/components/TambaLogo";
import ThemeToggle from "@/components/ThemeToggle";
import {
  CreditCard,
  ShieldCheck,
  TrendingUp,
  PiggyBank,
  Landmark,
  HeartHandshake,
  Home,
  Calculator,
  FileText,
  Briefcase,
  ArrowLeft,
  ArrowRight,
  Check,
  Sparkles,
} from "lucide-react";

const services = [
  {
    id: "debt-management",
    icon: CreditCard,
    title: "Debt Management",
    tagline: "Take control of your debt, not the other way around",
    description:
      "Our AI analyzes your debt portfolio and creates a personalized payoff strategy. Whether it's credit cards, student loans, or mortgages — we find the fastest, cheapest path to being debt-free.",
    features: [
      "Debt snowball & avalanche strategy comparison",
      "Interest savings calculator",
      "Automated payment scheduling recommendations",
      "Debt consolidation analysis",
    ],
    color: "bg-red-500",
    lightBg: "bg-red-50 dark:bg-red-950/30",
    accent: "text-red-500",
    border: "border-red-200 dark:border-red-800/40",
  },
  {
    id: "insurance-risk",
    icon: ShieldCheck,
    title: "Insurance & Risk Management",
    tagline: "Protect what matters most",
    description:
      "Comprehensive risk assessment and insurance optimization. We evaluate your current coverage, identify gaps, and recommend policies that align with your life stage and financial goals.",
    features: [
      "Coverage gap analysis",
      "Premium optimization recommendations",
      "Life, health, and property risk assessment",
      "Claims support guidance",
    ],
    color: "bg-tamba-blue",
    lightBg: "bg-blue-50 dark:bg-blue-950/30",
    accent: "text-tamba-blue",
    border: "border-blue-200 dark:border-blue-800/40",
  },
  {
    id: "investments",
    icon: TrendingUp,
    title: "Investments",
    tagline: "Grow your wealth with confidence",
    description:
      "From ETFs and stocks to private equity and venture capital — access AI-powered portfolio management tailored to your risk tolerance, time horizon, and financial goals.",
    features: [
      "AI-driven portfolio rebalancing",
      "Risk-adjusted return optimization",
      "Access to private & alternative investments",
      "Real-time market insights",
    ],
    color: "bg-tamba-teal",
    lightBg: "bg-teal-50 dark:bg-teal-950/30",
    accent: "text-tamba-teal",
    border: "border-teal-200 dark:border-teal-800/40",
  },
  {
    id: "pension-funds",
    icon: PiggyBank,
    title: "Voluntary Pension Funds",
    tagline: "Secure your future, starting today",
    description:
      "Maximize your voluntary pension contributions with smart allocation strategies. We help you choose the right funds and contribution levels to build a comfortable retirement nest egg.",
    features: [
      "Contribution optimization calculator",
      "Fund performance comparison",
      "Tax-advantaged strategy planning",
      "Retirement readiness scoring",
    ],
    color: "bg-pink-500",
    lightBg: "bg-pink-50 dark:bg-pink-950/30",
    accent: "text-pink-500",
    border: "border-pink-200 dark:border-pink-800/40",
  },
  {
    id: "retirement-income",
    icon: Landmark,
    title: "Retirement Incomes",
    tagline: "Enjoy the retirement you deserve",
    description:
      "Plan your retirement income streams with precision. We model scenarios, project income needs, and build sustainable withdrawal strategies so your money lasts as long as you do.",
    features: [
      "Income stream modeling & projections",
      "Sustainable withdrawal rate planning",
      "Social security optimization",
      "Lifestyle-adjusted retirement forecasting",
    ],
    color: "bg-amber-500",
    lightBg: "bg-amber-50 dark:bg-amber-950/30",
    accent: "text-amber-500",
    border: "border-amber-200 dark:border-amber-800/40",
  },
  {
    id: "aged-care",
    icon: HeartHandshake,
    title: "Aged Care",
    tagline: "Compassionate planning for the years ahead",
    description:
      "Navigate the complex landscape of aged care with clarity. We help you understand costs, eligibility, and funding options to ensure quality care without depleting your assets.",
    features: [
      "Aged care cost estimator",
      "Government benefit eligibility assessment",
      "Asset protection strategies",
      "Family financial impact analysis",
    ],
    color: "bg-purple-500",
    lightBg: "bg-purple-50 dark:bg-purple-950/30",
    accent: "text-purple-500",
    border: "border-purple-200 dark:border-purple-800/40",
  },
  {
    id: "estate-planning",
    icon: Home,
    title: "Estate Planning",
    tagline: "Leave a legacy, not a burden",
    description:
      "Protect your family's future with thoughtful estate planning. From wills and trusts to succession strategies, we ensure your wealth is transferred efficiently and according to your wishes.",
    features: [
      "Will and trust guidance",
      "Beneficiary optimization",
      "Tax-efficient wealth transfer strategies",
      "Power of attorney and guardianship planning",
    ],
    color: "bg-indigo-500",
    lightBg: "bg-indigo-50 dark:bg-indigo-950/30",
    accent: "text-indigo-500",
    border: "border-indigo-200 dark:border-indigo-800/40",
  },
  {
    id: "cashflow-budgeting",
    icon: Calculator,
    title: "Cashflow Management & Budgeting",
    tagline: "Every dollar has a purpose",
    description:
      "AI-powered budgeting that adapts to your life. Track income, monitor spending, and get real-time recommendations to optimize your cash flow and accelerate savings.",
    features: [
      "AI-generated personalized budgets",
      "Automated expense categorization",
      "Spending pattern insights",
      "Savings acceleration recommendations",
    ],
    color: "bg-tamba-coral",
    lightBg: "bg-orange-50 dark:bg-orange-950/30",
    accent: "text-tamba-coral",
    border: "border-orange-200 dark:border-orange-800/40",
  },
  {
    id: "taxation-planning",
    icon: FileText,
    title: "Taxation Planning",
    tagline: "Keep more of what you earn",
    description:
      "Strategic tax planning to minimize your tax burden legally and ethically. We identify deductions, credits, and structuring opportunities specific to your financial situation.",
    features: [
      "Tax deduction identification",
      "Year-round tax strategy optimization",
      "Capital gains management",
      "Multi-year tax projection modeling",
    ],
    color: "bg-emerald-500",
    lightBg: "bg-emerald-50 dark:bg-emerald-950/30",
    accent: "text-emerald-500",
    border: "border-emerald-200 dark:border-emerald-800/40",
  },
  {
    id: "salary-packaging",
    icon: Briefcase,
    title: "Salary Packaging",
    tagline: "Maximize your take-home pay",
    description:
      "Unlock the full potential of salary packaging. We help you structure your remuneration to take advantage of tax benefits, increasing your effective income without a pay rise.",
    features: [
      "Salary packaging benefit calculator",
      "FBT impact analysis",
      "Novated lease comparison",
      "Take-home pay optimization",
    ],
    color: "bg-cyan-600",
    lightBg: "bg-cyan-50 dark:bg-cyan-950/30",
    accent: "text-cyan-600",
    border: "border-cyan-200 dark:border-cyan-800/40",
  },
];

const ServicesPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background" data-testid="services-page">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-background/90 backdrop-blur-xl border-b border-border/30">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <button onClick={() => navigate("/")} className="flex items-center">
              <TambaLogo className="h-12 w-auto" />
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2 sm:gap-4"
          >
            <ThemeToggle />
            <Button
              variant="ghost"
              onClick={() => navigate("/pricing")}
              className="font-medium"
              data-testid="services-nav-pricing-btn"
            >
              Pricing
            </Button>
            <Button
              onClick={() => navigate("/auth")}
              className="rounded-full px-6 bg-tamba-blue hover:bg-tamba-blue/90 shadow-lg shadow-tamba-blue/25"
              data-testid="services-nav-get-started-btn"
            >
              Get Started
            </Button>
          </motion.div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1 }}
            className="absolute top-10 right-[15%] w-80 h-80 bg-tamba-blue/15 rounded-full blur-3xl"
          />
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="absolute bottom-0 left-[10%] w-96 h-96 bg-tamba-teal/10 rounded-full blur-3xl"
          />
        </div>

        <div className="max-w-5xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="mb-6 text-muted-foreground"
              data-testid="services-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
            </Button>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
              Our{" "}
              <span className="text-tamba-blue">Financial</span>{" "}
              <span className="text-tamba-teal">Services</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
              Comprehensive financial advice and planning across 10 critical areas — powered by AI, delivered with care.
            </p>
            <p className="text-sm font-medium text-tamba-teal tracking-widest uppercase">
              Your Complete Wealth Partner
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto space-y-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: index * 0.05 }}
              data-testid={`service-card-${service.id}`}
            >
              <Card
                className={`overflow-hidden border ${service.border} hover:shadow-xl transition-all duration-300`}
              >
                <CardContent className="p-0">
                  <div className={`flex flex-col lg:flex-row ${index % 2 !== 0 ? "lg:flex-row-reverse" : ""}`}>
                    {/* Left/Right accent strip */}
                    <div
                      className={`${service.color} w-full lg:w-2 h-2 lg:h-auto flex-shrink-0`}
                    />

                    {/* Icon section */}
                    <div
                      className={`${service.lightBg} flex items-center justify-center p-8 lg:p-12 lg:w-48 flex-shrink-0`}
                    >
                      <div
                        className={`w-20 h-20 rounded-2xl ${service.color} flex items-center justify-center shadow-lg`}
                      >
                        <service.icon className="w-10 h-10 text-white" />
                      </div>
                    </div>

                    {/* Content section */}
                    <div className="flex-1 p-6 lg:p-8">
                      <div className="flex items-start justify-between mb-1">
                        <span className={`text-xs font-bold uppercase tracking-wider ${service.accent}`}>
                          {String(index + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <h3 className="font-heading text-xl lg:text-2xl font-bold text-foreground mb-1">
                        {service.title}
                      </h3>
                      <p className={`text-sm font-medium ${service.accent} mb-3`}>
                        {service.tagline}
                      </p>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {service.features.map((feature) => (
                          <div
                            key={feature}
                            className="flex items-start gap-2"
                          >
                            <Check
                              className={`w-4 h-4 mt-0.5 flex-shrink-0 ${service.accent}`}
                            />
                            <span className="text-sm text-foreground/80">
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                      <Button
                        onClick={() => navigate(`/services/${service.id}`)}
                        className={`mt-4 rounded-full ${service.color} hover:opacity-90 text-white`}
                        data-testid={`explore-${service.id}-btn`}
                      >
                        Explore Service <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="rounded-2xl bg-tamba-blue overflow-hidden shadow-2xl shadow-tamba-blue/30 relative">
              <div className="p-10 md:p-14 text-center text-white relative">
                <div className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
                <div className="absolute bottom-0 right-0 w-48 h-48 bg-white/10 rounded-full translate-x-1/4 translate-y-1/4" />
                <div className="relative">
                  <Sparkles className="w-10 h-10 mx-auto mb-4 text-tamba-yellow" />
                  <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-white">
                    Ready to Get Started?
                  </h2>
                  <p className="text-white/90 mb-6 max-w-xl mx-auto">
                    Start your free trial and get personalized guidance across all 10 service areas from your AI wealth coach.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      size="lg"
                      onClick={() => navigate("/auth")}
                      className="rounded-full px-10 py-6 font-bold bg-white text-tamba-blue hover:bg-white/90 shadow-xl transition-all hover:scale-105"
                      data-testid="services-cta-btn"
                    >
                      Start Free Trial
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={() => navigate("/pricing")}
                      className="rounded-full px-10 py-6 font-bold border-white/30 text-white hover:bg-white/10"
                      data-testid="services-pricing-btn"
                    >
                      View Pricing
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-border/30">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <TambaLogo className="h-10 w-auto" />
          <p className="text-sm text-muted-foreground">
            © 2026 Tamba. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ServicesPage;
